import { useEffect, useRef, useState, useCallback } from 'react';

// =============================================================================
//  R2 Enterprise Splash Screen — Signature First Impression
//  Modular boot steps, animated wireframes, rotating project cards
//  60 FPS optimized · enterprise aesthetic · Skip on return visit
// =============================================================================

// ── Constants ─────────────────────────────────────────────────────────────────
const VERSION   = { major: '1.0', build: '2026.07', edition: 'Enterprise' };
const USER_NAME = 'John Bismonte, RCDD';
const LS_SKIP   = 'r2_splash_seen';
const LS_ALLOW  = 'r2_splash_always_show';

// Modular boot steps — add new engines here; splash auto-includes them
const BOOT_STEPS = [
  { id: 'ai-engine',        label: 'AI Engine',                    status: 'ONLINE'        },
  { id: 'knowledge-graph',  label: 'Knowledge Graph',              status: 'SYNCHRONIZED'  },
  { id: 'adaptive-bim',     label: 'Adaptive BIM Engine',          status: 'ONLINE'        },
  { id: 'r2dl',             label: 'R2DL Interpreter',             status: 'READY'         },
  { id: 'document-intel',   label: 'Document Intelligence',        status: 'ONLINE'        },
  { id: 'coordination',     label: 'Coordination Engine',          status: 'ONLINE'        },
  { id: 'simulation',       label: 'Simulation Engine',            status: 'ONLINE'        },
  { id: 'digital-twin',     label: 'Digital Twin Engine',          status: 'SYNCHRONIZED'  },
  { id: 'marketplace',      label: 'Marketplace',                  status: 'CONNECTED'     },
  { id: 'security',         label: 'Security Engine',              status: 'ONLINE'        },
];

const SYSTEM_SUMMARY = {
  ai_engines:       9,
  engineering_rules: 14532,
  building_types:   128,
  license:          'Enterprise',
  ai_health:        'Online',
};

// Flagship projects rotating card
const FLAGSHIP_PROJECTS = [
  { name: 'Global Aviation Command Hub',      type: 'International Infrastructure', icon: '✈' },
  { name: 'Hyperscale Data Centre Campus',    type: 'Tier IV Mission Critical',     icon: '▪' },
  { name: '100-Storey Mixed-Use Tower',       type: 'Supertall Vertical City',      icon: '▲' },
  { name: 'Smart Hospital Campus',            type: 'Level 1 Trauma Healthcare',    icon: '+' },
  { name: 'University Research Campus',       type: 'Academic & Research',          icon: '◈' },
  { name: 'Semiconductor Manufacturing Fab',  type: 'Cleanroom Facility',           icon: '◆' },
  { name: 'National Stadium Complex',         type: '80,000 Seat Sports Venue',     icon: '○' },
  { name: 'Metro Transit Hub',                type: 'Urban Transit Station',        icon: '▶' },
  { name: 'Government Operations Center',     type: 'Secure Government Facility',   icon: '⬡' },
  { name: 'Smart Industrial Factory',         type: 'Industry 4.0 Manufacturing',   icon: '⬛' },
  { name: 'Luxury Hotel & Convention',        type: '5-Star Hotel Complex',         icon: '◇' },
  { name: 'Smart Residential Community',      type: '2,400-Unit Development',       icon: '⬜' },
  { name: 'Sports Performance Campus',        type: 'Elite Training Facility',      icon: '◉' },
  { name: 'Space Research Facility',          type: 'Aerospace R&D Complex',        icon: '✦' },
];

// SVG wireframe silhouettes per building type
const WIREFRAMES = [
  // Airport
  `<g opacity="0.07" stroke="currentColor" fill="none" strokeWidth="1">
    <rect x="40" y="140" width="320" height="60" rx="2"/>
    <rect x="60" y="120" width="280" height="25" rx="1"/>
    <line x1="100" y1="140" x2="100" y2="200"/><line x1="160" y1="140" x2="160" y2="200"/>
    <line x1="220" y1="140" x2="220" y2="200"/><line x1="280" y1="140" x2="280" y2="200"/>
    <line x1="320" y1="140" x2="320" y2="200"/>
    <path d="M160 120 L200 80 L240 120"/>
    <rect x="185" y="60" width="30" height="20" rx="1"/>
    <line x1="200" y1="60" x2="200" y2="40"/>
  </g>`,
  // Data Centre
  `<g opacity="0.07" stroke="currentColor" fill="none" strokeWidth="1">
    <rect x="60" y="100" width="280" height="100" rx="2"/>
    <rect x="80" y="115" width="240" height="12" rx="1"/>
    <rect x="80" y="135" width="240" height="12" rx="1"/>
    <rect x="80" y="155" width="240" height="12" rx="1"/>
    <rect x="80" y="175" width="240" height="12" rx="1"/>
    <line x1="200" y1="100" x2="200" y2="200"/>
    <rect x="90" y="118" width="3" height="6" rx="0.5"/><rect x="97" y="118" width="3" height="6" rx="0.5"/>
    <rect x="104" y="118" width="3" height="6" rx="0.5"/>
  </g>`,
  // Tower
  `<g opacity="0.07" stroke="currentColor" fill="none" strokeWidth="1">
    <rect x="170" y="40" width="60" height="180" rx="2"/>
    <line x1="180" y1="60" x2="220" y2="60"/><line x1="180" y1="80" x2="220" y2="80"/>
    <line x1="180" y1="100" x2="220" y2="100"/><line x1="180" y1="120" x2="220" y2="120"/>
    <line x1="180" y1="140" x2="220" y2="140"/><line x1="180" y1="160" x2="220" y2="160"/>
    <line x1="180" y1="180" x2="220" y2="180"/>
    <line x1="200" y1="40" x2="200" y2="20"/>
    <rect x="140" y="200" width="120" height="20" rx="1"/>
  </g>`,
  // Hospital
  `<g opacity="0.07" stroke="currentColor" fill="none" strokeWidth="1">
    <rect x="80" y="120" width="240" height="80" rx="2"/>
    <rect x="160" y="80" width="80" height="40" rx="1"/>
    <rect x="170" y="85" width="60" height="30" rx="1"/>
    <path d="M185 95 h10 v-10 h10 v10 h10 v10 h-10 v10 h-10 v-10 h-10 z" fill="currentColor"/>
    <line x1="120" y1="120" x2="120" y2="200"/><line x1="200" y1="120" x2="200" y2="200"/>
    <line x1="280" y1="120" x2="280" y2="200"/>
  </g>`,
];

// ── Component ─────────────────────────────────────────────────────────────────
export function Splash({ onDone = () => {} }: { onDone?: () => void }) {
  const [stepsDone, setStepsDone]         = useState<Set<number>>(new Set());
  const [currentStep, setCurrentStep]     = useState(0);
  const [bootComplete, setBootComplete]   = useState(false);
  const [projectIdx, setProjectIdx]       = useState(0);
  const [wireframeIdx, setWireframeIdx]   = useState(0);
  const [showWelcome, setShowWelcome]     = useState(false);
  const [exiting, setExiting]             = useState(false);
  const [skippable, setSkippable]         = useState(false);
  const hasSeenRef                        = useRef(false);

  // Check if user has seen splash before
  const hasSeen    = typeof localStorage !== 'undefined' && localStorage.getItem(LS_SKIP) === '1';
  const alwaysShow = typeof localStorage !== 'undefined' && localStorage.getItem(LS_ALLOW) === '1';

  // Fast-track on return visit (unless user enabled "always show")
  const STEP_DELAY = hasSeen && !alwaysShow ? 60 : 220;
  const TOTAL_DELAY = hasSeen && !alwaysShow ? 1200 : BOOT_STEPS.length * STEP_DELAY + 800;

  const finish = useCallback(() => {
    if (exiting) return;
    setExiting(true);
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_SKIP, '1');
    setTimeout(onDone, 600);
  }, [exiting, onDone]);

  // Boot sequence — reveal steps one at a time
  useEffect(() => {
    if (bootComplete) return;
    if (currentStep >= BOOT_STEPS.length) {
      setBootComplete(true);
      setShowWelcome(true);
      setTimeout(finish, 2200);
      return;
    }
    const t = setTimeout(() => {
      setStepsDone(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => prev + 1);
    }, STEP_DELAY);
    return () => clearTimeout(t);
  }, [currentStep, bootComplete, STEP_DELAY, finish]);

  // Show skip button after 1s
  useEffect(() => {
    const t = setTimeout(() => setSkippable(true), 1000);
    return () => clearTimeout(t);
  }, []);

  // Rotate flagship project card every 3.5s
  useEffect(() => {
    const t = setInterval(() => setProjectIdx(prev => (prev + 1) % FLAGSHIP_PROJECTS.length), 3500);
    return () => clearInterval(t);
  }, []);

  // Rotate wireframe every 4s
  useEffect(() => {
    const t = setInterval(() => setWireframeIdx(prev => (prev + 1) % WIREFRAMES.length), 4000);
    return () => clearInterval(t);
  }, []);

  const proj = FLAGSHIP_PROJECTS[projectIdx];
  if (!proj) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#02040a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
        transition: 'opacity 0.6s ease',
        opacity: exiting ? 0 : 1,
        pointerEvents: exiting ? 'none' : 'auto',
        userSelect: 'none',
        overflow: 'hidden',
      }}
    >
      {/* ── Animated wireframe background ─────────────────────────────────── */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', color: '#5eead4', pointerEvents: 'none', transition: 'opacity 1.5s ease' }}
        viewBox="0 0 400 260"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Grid lines */}
        <g opacity="0.03" stroke="#5eead4" strokeWidth="0.5">
          {Array.from({ length: 20 }, (_, i) => (
            <line key={`v${i}`} x1={i * 21} y1="0" x2={i * 21} y2="260" />
          ))}
          {Array.from({ length: 14 }, (_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 20} x2="400" y2={i * 20} />
          ))}
        </g>
        {/* Corner marks */}
        <g opacity="0.12" stroke="#5eead4" strokeWidth="1" fill="none">
          <path d="M10 30 L10 10 L30 10" /><path d="M370 10 L390 10 L390 30" />
          <path d="M10 230 L10 250 L30 250" /><path d="M370 250 L390 250 L390 230" />
        </g>
        {/* Wireframe building silhouette */}
        <g dangerouslySetInnerHTML={{ __html: WIREFRAMES[wireframeIdx] ?? '' }} />
        {/* Subtle radial glow */}
        <defs>
          <radialGradient id="glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#5eead4" stopOpacity="0.04" />
            <stop offset="100%" stopColor="#5eead4" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="200" cy="130" rx="200" ry="130" fill="url(#glow)" />
      </svg>

      {/* ── Rotating project card ─────────────────────────────────────────── */}
      <div
        key={projectIdx}
        style={{
          position: 'absolute', bottom: 24, left: 24,
          background: 'rgba(94,234,212,0.04)',
          border: '1px solid rgba(94,234,212,0.12)',
          borderRadius: 8, padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 10,
          animation: 'fadeSlideUp 0.8s ease both',
          maxWidth: 320,
        }}
      >
        <span style={{ fontSize: 16, color: 'rgba(94,234,212,0.6)' }}>{proj.icon}</span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(241,245,249,0.85)', letterSpacing: '0.02em' }}>{proj.name}</div>
          <div style={{ fontSize: 9, color: 'rgba(100,116,139,0.9)', marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{proj.type}</div>
        </div>
      </div>

      {/* ── Skip button ───────────────────────────────────────────────────── */}
      {skippable && !bootComplete && (
        <button
          data-testid="splash-skip-btn"
          onClick={finish}
          style={{
            position: 'absolute', top: 20, right: 20,
            background: 'transparent',
            border: '1px solid rgba(100,116,139,0.3)',
            borderRadius: 6, padding: '5px 14px',
            fontSize: 10, color: 'rgba(100,116,139,0.7)',
            letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 0.2s',
            animation: 'fadeIn 0.5s ease both',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#5eead4'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(94,234,212,0.4)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(100,116,139,0.7)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(100,116,139,0.3)'; }}
        >
          Skip Intro
        </button>
      )}

      {/* ── Version badge ─────────────────────────────────────────────────── */}
      <div style={{ position: 'absolute', top: 20, left: 24, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 9, color: 'rgba(94,234,212,0.5)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          R2 {VERSION.edition} · v{VERSION.major} · Build {VERSION.build}
        </span>
      </div>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 560, padding: '0 24px' }}>

        {/* Logo + Brand */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 72, height: 72, borderRadius: 16,
            background: 'linear-gradient(135deg, rgba(94,234,212,0.15), rgba(94,234,212,0.05))',
            border: '1px solid rgba(94,234,212,0.25)',
            marginBottom: 16,
            boxShadow: '0 0 40px rgba(94,234,212,0.08)',
          }}>
            <span style={{ fontSize: 32, fontWeight: 900, color: '#5eead4', letterSpacing: '-2px' }}>R²</span>
          </div>

          <div style={{ fontSize: 22, fontWeight: 800, color: 'rgba(241,245,249,0.95)', letterSpacing: '0.04em', marginBottom: 6 }}>
            BIM OPS
          </div>
          <div style={{ fontSize: 11, color: 'rgba(100,116,139,0.8)', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            AI-Powered BIM &amp; Infrastructure Platform
          </div>
        </div>

        {/* Boot steps */}
        <div style={{ marginBottom: 28 }}>
          {BOOT_STEPS.map((step, i) => {
            const done    = stepsDone.has(i);
            const active  = i === currentStep;
            const pending = !done && !active;
            return (
              <div
                key={step.id}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '5px 0',
                  opacity: done ? 0.9 : (active ? 1 : 0.25),
                  transition: 'opacity 0.3s ease',
                  animation: done ? 'none' : (active ? 'pulse 1s ease infinite' : 'none'),
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: done ? '#5eead4' : (active ? '#f59e0b' : 'rgba(100,116,139,0.3)'),
                    boxShadow: done ? '0 0 6px rgba(94,234,212,0.6)' : (active ? '0 0 6px rgba(245,158,11,0.6)' : 'none'),
                    transition: 'all 0.3s ease',
                    flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 11, color: done ? 'rgba(241,245,249,0.8)' : (active ? 'rgba(245,158,11,0.9)' : 'rgba(100,116,139,0.5)'), letterSpacing: '0.06em' }}>
                    {step.label}
                  </span>
                </div>
                <span style={{
                  fontSize: 9, letterSpacing: '0.14em', fontWeight: 700,
                  color: done ? '#5eead4' : (active ? '#f59e0b' : 'transparent'),
                }}>
                  {done ? step.status : (active ? '···' : '')}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 1, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, rgba(94,234,212,0.4), rgba(94,234,212,0.8))',
            borderRadius: 1,
            width: `${(currentStep / BOOT_STEPS.length) * 100}%`,
            transition: 'width 0.25s ease',
            boxShadow: '0 0 8px rgba(94,234,212,0.4)',
          }} />
        </div>

        {/* System summary */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
          padding: '12px 0', borderTop: '1px solid rgba(255,255,255,0.05)',
          borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: 20,
        }}>
          {[
            { label: 'AI Engines',        value: SYSTEM_SUMMARY.ai_engines },
            { label: 'Engineering Rules', value: SYSTEM_SUMMARY.engineering_rules.toLocaleString() },
            { label: 'Building Types',    value: SYSTEM_SUMMARY.building_types },
            { label: 'License',           value: SYSTEM_SUMMARY.license },
            { label: 'AI Health',         value: SYSTEM_SUMMARY.ai_health },
            { label: 'R2DL',              value: 'Ready' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', padding: '4px 0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(241,245,249,0.75)' }}>{s.value}</div>
              <div style={{ fontSize: 8, color: 'rgba(100,116,139,0.7)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Welcome / mission statement */}
        <div style={{ textAlign: 'center', minHeight: 52 }}>
          {showWelcome ? (
            <div style={{ animation: 'fadeIn 0.8s ease both' }}>
              <div style={{ fontSize: 13, color: 'rgba(241,245,249,0.9)', fontWeight: 600, marginBottom: 6 }}>
                Welcome, {USER_NAME}.
              </div>
              <div style={{ fontSize: 11, color: '#5eead4', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                R2 Enterprise is Ready.
              </div>
            </div>
          ) : bootComplete ? null : (
            <div style={{ fontSize: 10, color: 'rgba(100,116,139,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', animation: 'fadeIn 1s ease both' }}>
              Design · Coordinate · Simulate · Build
            </div>
          )}
        </div>

      </div>

      {/* R2DL identity line */}
      <div style={{
        position: 'absolute', bottom: 28, right: 24,
        fontSize: 9, color: 'rgba(100,116,139,0.5)',
        letterSpacing: '0.1em', textTransform: 'uppercase',
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3,
      }}>
        <span>Engineering Intent Engine</span>
        <span style={{ color: 'rgba(94,234,212,0.45)' }}>R2DL Interpreter · Ready</span>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }
      `}</style>
    </div>
  );
}
